/**
 * L'OSPITE — chi apre la Mini App da Telegram SENZA aver fatto login.
 *
 * [Luca, I-193, 2026-09-25 ~21:47Z, testuale: «crea nuova sessione dedicata tma limulo per
 * permettere anche ai guest di entrare senza dover far login!»]
 *
 * Fino a qui l'ospite non entrava da nessuna parte: `AppShell` rendeva `<Login />` al posto
 * dell'intero albero delle rotte, News comprese. Con `VITE_FEATURE_GUEST_ENABLED` (OPT-IN, acceso
 * solo in `.env.staging`) entra, guarda cio' che e' pubblico, e il login gli viene chiesto al
 * PRIMO GESTO che lo richiede.
 *
 * ═══ PERCHE' QUESTO FILE E' PURO ═══
 * Niente `import.meta.env`, niente alias `@/`, niente React: le tre decisioni che contano —
 * quali richieste escono, quali rotte si aprono, dove si torna dopo il login — si esercitano
 * sotto Node da `tools/verify-ospite.ts`. L'interruttore lo legge chi chiama.
 *
 * ═══ LE TRE SCELTE DI PRODOTTO CHE QUI DENTRO SONO UNA RIGA (per poterle cambiare) ═══
 *  · Q1 «primo avvio = Hub diretta»: e' `/` in `ROTTE_APERTE`. Un benvenuto, se Luca lo
 *    vorra', e' un componente in `AppShell` nel punto dove oggi c'era `<Login />`.
 *  · Q2 «tornei dietro Accedi nel lotto 1»: i tornei NON sono in `ROTTE_APERTE` e Limulo non ha
 *    voci in `LETTURE_PUBBLICHE`. Quando il BFF avra' le rotte pubbliche (bozza per @phomea,
 *    lotto 2) si aggiunge qui, non altrove.
 *  · Q3 «l'ospite non vede piu' di un loggato non-beta»: nessuna rotta dietro `RoleGate` e'
 *    in `ROTTE_APERTE`, e nessuna deve entrarci.
 */

/**
 * 🔑 IL predicato della sessione — lo stesso di `useLogtoAuth().isAuthenticated` (bridge || SDK),
 * letto dallo stato Redux (`auth.bridgeAuthenticated`, `auth.sdkAuthenticated`). UNO solo, e lo
 * usano tutti quelli che decidono se qualcuno e' un OSPITE: `useOspite` (e quindi `AppShell`) e la
 * guardia di rete (`services/guardiaOspite.ts`). Due definizioni diverse davano, nel ripiego web
 * (SDK vero, bridge falso), un utente loggato che entrava nell'app e si vedeva bloccare ogni
 * richiesta (revisione di #395).
 * Sta QUI e non in `store/auth.slice.ts`: lo slice, caricato, legge `localStorage` per il suo stato
 * iniziale, e la guardia non deve trascinarselo dietro nei test e nelle sonde sotto Node.
 * Fail-closed: uno stato senza `auth`, o con valori che non sono `true`, non ha sessione.
 */
export function haSessione(stato: unknown): boolean {
  const auth = (stato as { auth?: { bridgeAuthenticated?: unknown; sdkAuthenticated?: unknown } } | undefined)
    ?.auth;
  return auth?.bridgeAuthenticated === true || auth?.sdkAuthenticated === true;
}

/** Il servizio che fa la richiesta: la chiave che ogni client passa a `guardiaOspite`. */
export type Servizio =
  | 'monolite'
  | 'qnews'
  | 'qavatar'
  | 'customization'
  | 'qduels'
  | 'limulo'
  | 'qbot'
  | 'qbank'
  | 'qchinesepoker'
  | 'qchess'
  | 'qclans'
  | 'qriots'
  | 'quest'
  | 'qgammon'
  | 'qsocials';

/**
 * Le SOLE richieste che escono dal telefono di un ospite: GET, servizio, percorso.
 *
 * Ogni voce e' una lettura MISURATA senza `Authorization` [M 2026-09-25 21:5xZ, `qnews.qlash.gg`]:
 * `/qnews` 200, `/qnews/feed` 200, `/qnews/trending` 200, `/qnews/<slug>` 200, `/tags` 200,
 * `/tags/<slug>` 200, `/tags/<slug>/articles` 200 — controllo negativo `/preferences` 401, e uno
 * slug inventato 404 (quindi il 200 viene dalla rotta, non da un fallback).
 *
 * ⛔ Una voce entra qui SOLO se il servizio la serve davvero senza token. Una lettura che lo
 * richiede NON si «apre» dal telefono con un token di servizio (sarebbe pubblico nel bundle):
 * va chiesta al BFF come rotta pubblica. E' la ragione per cui Limulo qui non c'e'.
 * ⛔ Niente scritture, nemmeno «innocue»: `POST /qnews/:id/view` (il conteggio delle letture)
 * resta fuori. Un ospite non scrive niente, da nessuna parte.
 */
const LETTURE_PUBBLICHE: Readonly<Partial<Record<Servizio, readonly RegExp[]>>> = {
  qnews: [
    /^\/qnews$/,
    /^\/qnews\/feed$/,
    /^\/qnews\/trending$/,
    /^\/qnews\/[^/]+$/,
    /^\/tags$/,
    /^\/tags\/[^/]+$/,
    /^\/tags\/[^/]+\/articles$/,
  ],
};

/** La forma degli `args` di un baseQuery RTK, quanto basta per decidere. */
export type Richiesta = string | { url: string; method?: string };

/**
 * `true` se un ospite puo' fare questa richiesta. Tutto cio' che non e' in `LETTURE_PUBBLICHE`
 * e' `false` — fail-closed: un servizio nuovo, un percorso assoluto o un metodo diverso da GET
 * non escono finche' qualcuno non li scrive qui.
 */
export function letturaPubblica(servizio: Servizio, richiesta: Richiesta): boolean {
  const url = typeof richiesta === 'string' ? richiesta : richiesta.url;
  const metodo = typeof richiesta === 'string' ? 'GET' : (richiesta.method ?? 'GET');
  if (metodo.toUpperCase() !== 'GET') return false;
  if (typeof url !== 'string') return false;
  const percorso = url.split(/[?#]/, 1)[0] ?? '';
  if (percorso.includes('..')) return false;
  const regole = LETTURE_PUBBLICHE[servizio] ?? [];
  return regole.some((r) => r.test(percorso));
}

/**
 * Le schermate che un ospite vede. Tutte le altre mostrano «Accedi» al posto del contenuto.
 * Hub (con le sezioni che dicono «Accedi») e News (dati veri, pubblici).
 */
const ROTTE_APERTE: readonly RegExp[] = [
  /^\/$/,
  /^\/news$/,
  /^\/news\/tag\/[^/]+$/,
  /^\/news\/[^/]+$/,
  // QBridge R1 K9a, «la prima mano senza account» (L-3002): l'ingresso, la prima mano e le lezioni. Senza queste righe l'ospite non
  // arriva MAI alle schermate: `AppShell` gli mette «Accedi» al posto di <Outlet /> prima che `CancelloBridge` possa lasciarlo
  // passare. Solo queste: la smazzata dei robot di R0 (`/bridge/smazzata`) e tutto cio' che verra' dopo restano chiusi. Le rotte
  // esistono solo con `VITE_FEATURE_BRIDGE_ENABLED` (staging): altrove il router non le ha e il jolly riporta sulla Hub.
  /^\/bridge$/,
  /^\/bridge\/prima-mano$/,
  /^\/bridge\/lezioni$/,
  /^\/bridge\/lezioni\/L(?:0[1-9]|1[0-3])$/,
  // K9a-2: le rotte che GIOCANO senza rete (condizione 1/2 di tma-orcr). Una regex CHIUSA per rotta, coi parametri dell'elenco vero (le 13 lezioni,
  // le mani da 1 a 8): nessun prefisso aperto. La pratica LIBERA (E1), il tavolo e la smazzata dei robot restano chiusi.
  /^\/bridge\/lezioni\/L(?:0[1-9]|1[0-3])\/pratica\/[1-8]$/,
  /^\/bridge\/minibridge$/,
  /^\/bridge\/minibridge\/[1-8]$/,
];

/** Combaciano con `/news/:slug` ma sono dati PERSONALI: le preferenze del feed. */
const ROTTE_CHIUSE: readonly string[] = ['/news/preferences'];

export function rottaApertaAllOspite(pathname: string): boolean {
  const p = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  if (ROTTE_CHIUSE.includes(p)) return false;
  return ROTTE_APERTE.some((r) => r.test(p));
}

// ═══ DOVE SI TORNA DOPO IL LOGIN ═══
//
// [M 2026-09-25, letto nel codice] Il login riporta SEMPRE su `/`: dal telefono Telegram riapre
// la Mini App principale con `startapp=auth_<code>` (`routes/AuthCallback.tsx`, BridgeCallback),
// da desktop la callback fa `location.replace('/')`. L'ospite che toccava «Iscriviti» si
// ritroverebbe sulla Hub senza sapere perche'. Qui si ricorda la SCHERMATA — non il gesto: un
// «Iscriviti» rieseguito da solo dopo il login sarebbe una scrittura che nessuno ha confermato.
//
// localStorage e non sessionStorage: dal telefono il rientro e' una WebView NUOVA (ci stanno gia'
// i token, per la stessa ragione). Stesso schema e stessa durata dello stash degli inviti
// (`lib/startParam.ts`).

export const CHIAVE_RITORNO = 'qlash.ospite.ritorno';
export const RITORNO_TTL_MS = 30 * 60 * 1000;

/** Il sottoinsieme di `Storage` che serve: iniettato, cosi' Node lo esercita senza un browser. */
export interface Archivio {
  getItem(chiave: string): string | null;
  setItem(chiave: string, valore: string): void;
  removeItem(chiave: string): void;
}

interface VoceRitorno {
  percorso: string;
  salvatoIl: number;
}

/**
 * Solo percorsi INTERNI all'app. `//host` e `\` sono i due modi classici di far uscire un
 * «percorso relativo» verso un altro dominio; `/auth/*` e' il giro di login stesso, tornarci
 * lo ricomincerebbe. `/` non si salva: e' gia' dove si atterra.
 */
export function ritornoAmmesso(percorso: unknown): percorso is string {
  return (
    typeof percorso === 'string' &&
    percorso.length > 1 &&
    percorso.length <= 512 &&
    percorso.startsWith('/') &&
    !percorso.startsWith('//') &&
    !percorso.includes('\\') &&
    !percorso.startsWith('/auth')
  );
}

/** Ricorda dove tornare. `false` se il percorso non e' ammesso o lo storage non c'e'. */
export function salvaRitorno(archivio: Archivio, percorso: string, adesso: number): boolean {
  if (!ritornoAmmesso(percorso)) return false;
  try {
    const voce: VoceRitorno = { percorso, salvatoIl: adesso };
    archivio.setItem(CHIAVE_RITORNO, JSON.stringify(voce));
    return true;
  } catch {
    // Storage non disponibile: il login funziona lo stesso, si torna sulla Hub come prima.
    return false;
  }
}

/** Legge E cancella. Un ritorno si usa una volta sola; scaduto o malformato vale `null`. */
export function prendiRitorno(archivio: Archivio, adesso: number): string | null {
  let grezzo: string | null = null;
  try {
    grezzo = archivio.getItem(CHIAVE_RITORNO);
    archivio.removeItem(CHIAVE_RITORNO);
  } catch {
    return null;
  }
  if (!grezzo) return null;
  let voce: Partial<VoceRitorno>;
  try {
    voce = JSON.parse(grezzo) as Partial<VoceRitorno>;
  } catch {
    return null;
  }
  if (!voce || typeof voce.salvatoIl !== 'number') return null;
  const eta = adesso - voce.salvatoIl;
  if (eta < 0 || eta > RITORNO_TTL_MS) return null;
  return ritornoAmmesso(voce.percorso) ? voce.percorso : null;
}

/** Butta il ritorno senza usarlo (es. quando vince un invito). */
export function scartaRitorno(archivio: Archivio): void {
  try {
    archivio.removeItem(CHIAVE_RITORNO);
  } catch {
    /* scade comunque da solo */
  }
}

// ═══ IL BENVENUTO: la scelta «entra come ospite» si ricorda ═══
//
// [D Luca, 2026-09-25 ~23:2xZ, via tma-orcb: «C1 (b)»] Al primo avvio da ospite non si va dritti
// alla Hub: c'e' una schermata di benvenuto con due scelte, «Entra come ospite» e «Accedi».
// «Entra come ospite» si RICORDA qui, cosi' la schermata non torna a ogni apertura della Mini App;
// dal Profilo ospite la si puo' rivedere (`dimenticaSceltaOspite`).

export const CHIAVE_SCELTA_OSPITE = 'qlash.ospite.scelto';

/** `true` se questa persona ha gia' scelto di entrare come ospite. Storage illeggibile ⇒ `false`. */
export function sceltaOspiteRicordata(archivio: Archivio): boolean {
  try {
    return archivio.getItem(CHIAVE_SCELTA_OSPITE) === '1';
  } catch {
    return false;
  }
}

/** Ricorda la scelta. `false` se lo storage non la tiene (chi chiama ha un ripiego in memoria). */
export function ricordaSceltaOspite(archivio: Archivio): boolean {
  try {
    archivio.setItem(CHIAVE_SCELTA_OSPITE, '1');
    return true;
  } catch {
    return false;
  }
}

export function dimenticaSceltaOspite(archivio: Archivio): void {
  try {
    archivio.removeItem(CHIAVE_SCELTA_OSPITE);
  } catch {
    /* niente da dimenticare se lo storage non c'e' */
  }
}
